import { useEffect, useState, useCallback } from "react";
import moment from "moment";
import {
  LdsButton,
  LdsTable,
  LdsBadge,
  LdsLink,
} from "@elilillyco/ux-lds-react";

import CLoader from "../shared/CLoader";
import ctx from "../../plugins/axios";

const PRESETS = [
  { label: "Next 15 Days", days: 15 },
  { label: "Next 30 Days", days: 30 },
  { label: "Next 90 Days", days: 90 },
];

const ExpirationDateTab = () => {
  const today = moment().format("YYYY-MM-DD");
  const [expLoading, setExpLoading] = useState(false);
  const [allLists, setAllLists] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(
    moment().add(30, "days").format("YYYY-MM-DD")
  );
  const [activePreset, setActivePreset] = useState(30);

  const fetchExpirationLists = useCallback(async () => {
    setExpLoading(true);
    try {
      const { data } = await ctx.get(
        "/specialty_list?brand=&thpc_nm=&user_brands="
      );
      if (data?.data) {
        const current = data.data.current_data || [];
        const historical = data.data.history_data || [];
        setAllLists([...current, ...historical]);
      }
    } catch (err) {
      console.error("Failed to fetch specialty lists", err);
    } finally {
      setExpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allLists.length === 0) {
      fetchExpirationLists();
    }
  }, [fetchExpirationLists, allLists.length]);

  const applyPreset = (days) => {
    setActivePreset(days);
    setStartDate(today);
    setEndDate(moment().add(days, "days").format("YYYY-MM-DD"));
  };

  const filteredExpirationLists = allLists.filter((item) => {
    if (!item.expiration_date) return false;
    if ((item.list_status || "").toUpperCase() !== "APPROVED") return false;
    const exp = moment(item.expiration_date);
    return (
      exp.isSameOrAfter(moment(startDate).startOf("day")) &&
      exp.isSameOrBefore(moment(endDate).endOf("day"))
    );
  });

  const getDaysUntilExpiry = (expirationDate) => {
    const diff = moment(expirationDate).diff(moment().startOf("day"), "days");
    if (diff < 0) return "Expired";
    if (diff === 0) return "Expires today";
    return `${diff} day${diff !== 1 ? "s" : ""}`;
  };

  const getExpiryClass = (expirationDate) => {
    const diff = moment(expirationDate).diff(moment().startOf("day"), "days");
    if (diff < 0) return "expired";
    if (diff <= 15) return "expiring-soon";
    return "valid";
  };

  return (
    <div className="mt-3">
      <div className="row">
        <div className="col-12">
          <h2 className="pl-4">Expiration Date</h2>
        </div>
      </div>
      <div className="exp-filter-row">
        <div className="exp-date-pickers">
          <div className="exp-date-field">
            <label htmlFor="exp-start-date" className="exp-date-label">
              From
            </label>
            <div className="exp-date-wrapper">
              <span className="exp-date-icon">&#128197;</span>
              <input
                id="exp-start-date"
                type="date"
                value={startDate}
                min={today}
                max={endDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset(null);
                }}
                className="exp-date-input"
              />
            </div>
          </div>
          <span className="exp-divider">&#8594;</span>
          <div className="exp-date-field">
            <label htmlFor="exp-end-date" className="exp-date-label">
              To
            </label>
            <div className="exp-date-wrapper">
              <span className="exp-date-icon">&#128197;</span>
              <input
                id="exp-end-date"
                type="date"
                value={endDate}
                min={startDate > today ? startDate : today}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset(null);
                }}
                className="exp-date-input"
              />
            </div>
          </div>
        </div>
        <div className="exp-preset-buttons">
          <div className="exp-preset-group">
            <span className="exp-preset-label">Quick Select</span>
            <div className="exp-preset-btns">
              {PRESETS.map(({ label, days }) => (
                <LdsButton
                  key={days}
                  classes={activePreset === days ? "blue-btn" : ""}
                  onClick={() => applyPreset(days)}
                >
                  {label}
                </LdsButton>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {expLoading ? (
          <CLoader size={90} />
        ) : (
          <>
            <p className="exp-results-count">
              Showing{" "}
              <strong>{filteredExpirationLists.length}</strong>{" "}
              {filteredExpirationLists.length === 1 ? "list" : "lists"} expiring
              between{" "}
              <strong>{moment(startDate).format("MMM DD, YYYY")}</strong>
              {" "}and{" "}
              <strong>{moment(endDate).format("MMM DD, YYYY")}</strong>
            </p>

            {filteredExpirationLists.length === 0 ? (
              <div className="row">
                <span className="center">
                  <h2>No lists expiring in the selected date range.</h2>
                </span>
              </div>
            ) : (
              <LdsTable id="expiration-date-table" flat>
                <thead>
                  <tr>
                    <th>List Title</th>
                    <th>Brand/Molecule</th>
                    <th>Status</th>
                    <th>Expiration Date</th>
                    <th>Days Until Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpirationLists
                    .slice()
                    .sort((a, b) =>
                      moment(a.expiration_date).diff(
                        moment(b.expiration_date)
                      )
                    )
                    .map((item, i) => (
                      <tr key={i}>
                        <td>
                          {item.uuid ? (
                            <LdsLink href={`/details/${item.uuid}`}>
                              {item.list_title || "—"}
                            </LdsLink>
                          ) : (
                            item.list_title || "—"
                          )}
                        </td>
                        <td>{item.brand_nm || item.mdpr_nm || "—"}</td>
                        <td>
                          {item.list_status ? (
                            <LdsBadge
                              className={
                                item.list_status.toUpperCase() === "APPROVED"
                                  ? "green-btn"
                                  : "draft-badge"
                              }
                            >
                              {item.list_status.toUpperCase()}
                            </LdsBadge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {moment(item.expiration_date).format("MMM DD, YYYY")}
                        </td>
                        <td>
                          <span
                            className={`expiration-tag ${getExpiryClass(
                              item.expiration_date
                            )}`}
                          >
                            {getDaysUntilExpiry(item.expiration_date)}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </LdsTable>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExpirationDateTab;
